import { useMemo, type ReactNode } from 'react';

import type { RadialRingConfig } from '@/components/radialControlPad/root/radialControlPad.types.ts';

import { radialPadLayout } from './radialControlPad.constants.ts';
import {
  computeSectorLayouts,
  resolveSectorDisabled,
} from './useRadialPadRingContent/radialPadRing.functions.ts';
import { RadialSectorButton } from './useRadialPadRingContent/RadialSectorButton.tsx';
import { RadialSectorTooltip } from './useRadialPadRingContent/RadialSectorTooltip.tsx';

type RadialPadRingContent = {
  /** Pie sectors and icons (SVG). */
  svgNodes: ReactNode;
  /** Portaled ScaleUI tooltips (HTML overlay). */
  tooltipNodes: ReactNode;
};
export type UseRadialPadRingContentProps = {
  /** When false, returns `null` (e.g. optional outer ring). */
  enabled?: boolean;
  ring: RadialRingConfig;
  padRadius: number;
  sectorGapPx?: number;
  padDisabled?: boolean;
  holdRepeatDelayMs?: number;
  tooltipSectorId: string | null;
  onHoveredSectorChange: (sectorId: string | null) => void;
};
/**
 * Memoized SVG sector controls and HTML tooltip anchors for one ring.
 *
 * {@link RadialSectorButton} and {@link RadialSectorTooltip} are paired per sector but cannot
 * share one React parent: ScaleUI tooltips portal to `document.body` and must not live inside
 * `<svg>`. {@link RadialControlPad} places `svgNodes` under {@link RadialPadSvg} and
 * `tooltipNodes` under {@link RadialTooltipLayer}.
 */
export const useRadialPadRingContent = ({
  enabled = true,
  ring,
  padRadius,
  sectorGapPx,
  padDisabled,
  holdRepeatDelayMs,
  tooltipSectorId,
  onHoveredSectorChange,
}: UseRadialPadRingContentProps): RadialPadRingContent | null => {
  return useMemo(() => {
    if (!enabled) {
      return null;
    }

    const { iconSize = radialPadLayout.icon.innerSize } = ring;
    const layouts = computeSectorLayouts({ ring, padRadius, sectorGapPx });

    return {
      svgNodes: (
        <>
          {layouts.map(({ sector, iconX, iconY, sectorPath }) => (
            <RadialSectorButton
              key={sector.id}
              d={sectorPath}
              icon={sector.icon}
              label={sector.label}
              iconX={iconX}
              iconY={iconY}
              iconSize={iconSize}
              disabled={resolveSectorDisabled(sector.disabled, padDisabled)}
              holdRepeatDelayMs={holdRepeatDelayMs}
              onStart={sector.onStart}
              onStop={sector.onStop}
              onPointerEnter={() => onHoveredSectorChange(sector.id)}
              onPointerLeave={() => onHoveredSectorChange(null)}
            />
          ))}
        </>
      ),
      tooltipNodes: (
        <>
          {layouts.map(({ sector, tooltipLeft, tooltipTop }) => (
            <RadialSectorTooltip
              key={sector.id}
              label={sector.label}
              left={tooltipLeft}
              top={tooltipTop}
              open={!padDisabled && tooltipSectorId === sector.id}
            />
          ))}
        </>
      ),
    };
  }, [
    enabled,
    ring,
    padRadius,
    sectorGapPx,
    padDisabled,
    holdRepeatDelayMs,
    tooltipSectorId,
    onHoveredSectorChange,
  ]);
};
